import {
	DateString,
	Highlight,
	HighlightedText,
	ListItem,
	PersonAvatar
} from "client/Components";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { ReactElement } from "react";
import { useHighlights } from "../ResultView/CategoryView/ItemView/useHighlights";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { localize } from "client_legacy/ndaw/culture";
import { getUserProfileImageUrl, UserIdentityModel } from "client/Api/UserProfile/service";
import { HighlightItem } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { TimeZone } from "client/DateAndTime/timezones";
import { SubTitleSection } from "./SubTitleSection";
import styles from "./CommunityPostListItem.module.less";
import { useGlobalSearchItemActions } from "../GlobalSearchItem/useGlobalSearchItemActions";

export type CommunityPostListItemProps = {
	hitHighlights?: readonly HighlightItem[];
	id: number;
	createdBy: UserIdentityModel | null;
	createdOn: string;
	communityName: string;
	messagePreview: string | null;
	onRemove?: () => void;
	timezone: TimeZone;
	itemIndex: number;
	tabIndex: number;
	onExecute: () => void;
};

export const CommunityPostListItem = (props: CommunityPostListItemProps): ReactElement => {
	const {
		id,
		communityName,
		createdBy,
		messagePreview,
		hitHighlights = EMPTY_ARRAY,
		createdOn,
		onRemove,
		timezone,
		itemIndex,
		tabIndex,
		onExecute
	} = props;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const {
		onKeyDown,
		ref
	} = useGlobalSearchItemActions({
		itemIndex,
		onExecute
	});

	const {
		computedDisplayName,
		initials
	} = getComputedDisplayName(
		createdBy
			? createdBy
			: {
				displayName: localize("_DeletedUser")
			}
	);

	const avatarPhotoUrl: string | undefined = createdBy && isNotNil(createdBy.avatarId)
		? getUserProfileImageUrl(createdBy.avatarId)
		: undefined;

	return (
		<ListItem
			trackingId={generateTrackingId("community-post-list-item", `|${id}`)}
			noMobileBorderBottom
			tabIndex={tabIndex}
			ref={ref}
			onKeyDown={onKeyDown}
		>
			<ListItem.Content
				thumbnail={
					<div className={styles.avatar}>
						<PersonAvatar
							trackingId="author-avatar"
							initials={initials}
							imageSrc={avatarPhotoUrl}
							size="small"
						/>
					</div>
				}
			>
				<ListItem.Title
					trackingId="created-by"
				>
					{computedDisplayName}
				</ListItem.Title>
				<ListItem.SubTitle>
					<SubTitleSection
						trackingId="created-on"
						text={(
							<DateString
								format="RelativeDayWithTime"
								value={createdOn}
								timezone={timezone}
							/>
						)}
					/>
					<SubTitleSection
						trackingId="community"
						text={communityName}
					/>
				</ListItem.SubTitle>
				<div
					{...setTrackingId("message-preview")}
					className={styles.preview}
				>
					<HighlightedText
						text={messagePreview}
						highlight={highlights.get("messagePreview")}
					/>
				</div>
			</ListItem.Content>
			{isNotNil(onRemove) && (
				<ListItem.Action
					icon="xmark"
					onClick={() => onRemove()}
					bubbleClick={false}
					trackingId="remove"
				/>
			)}
		</ListItem>
	);
};
CommunityPostListItem.displayName = "CommunityPostListItem";